import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiPaginatedResponse, IApiResponse } from "@/types";
import {
  API_COMPANY_COMMISSIONS_BY_EMPLOYEE,
  API_COMPANY_COMMISSIONS_SUMMARY,
  API_COMPANY_COMMISSION_PLAN,
  API_COMPANY_COMMISSION_PLANS,
  API_COMPANY_CREATE_SALE,
  API_COMPANY_GET_SALES,
  API_COMPANY_GET_SALE_BY_ID,
  API_COMPANY_SALE_COMMISSION_OVERRIDE,
  API_COMPANY_SALE_COMMISSION_PREVIEW,
  API_COMPANY_SALE_DECISION,
  API_COMPANY_SALE_STATS,
} from "@/constants/apiEndpoints";
import type {
  ICommissionByEmployee,
  ICommissionOverridePayload,
  ICommissionPlan,
  ICommissionPreviewParams,
  ICommissionPreviewResult,
  ICommissionSummaryData,
  ICreateCommissionPlanPayload,
  ICreateSalePayload,
  IListCommissionsParams,
  IListSalesParams,
  ISale,
  ISaleDecisionPayload,
  ISaleListItem,
  ISaleStatsData,
  IUpdateCommissionPlanPayload,
} from "@/types/(company)/sales.interface";

export const companySalesApi = createApi({
  reducerPath: "companySalesApi",
  baseQuery: dynamicBaseQuery,
  refetchOnMountOrArgChange: true,
  tagTypes: [
    "companySales",
    "companySale",
    "companyCommissions",
    "commissionPlans",
    "commissionPlan",
  ],
  endpoints: (builder) => ({
    // ── Sales ───────────────────────────────────────────────────────────────
    companyGetSales: builder.query<
      IApiPaginatedResponse<ISaleListItem[]>,
      IListSalesParams
    >({
      query: (params) => ({ url: API_COMPANY_GET_SALES, params }),
      providesTags: ["companySales"],
    }),
    companyGetSaleStats: builder.query<
      IApiResponse<ISaleStatsData>,
      { branchId?: number; departmentId?: number } | void
    >({
      query: (params) => ({
        url: API_COMPANY_SALE_STATS,
        params: params ?? undefined,
      }),
      providesTags: ["companySales"],
    }),
    companyGetSaleById: builder.query<IApiResponse<ISale>, number>({
      query: (id) => ({ url: API_COMPANY_GET_SALE_BY_ID(id) }),
      providesTags: ["companySale"],
    }),

    /** Log a sale on behalf of an employee — pass `performedFor`. */
    companyCreateSale: builder.mutation<IApiResponse<ISale>, ICreateSalePayload>({
      query: (body) => ({ url: API_COMPANY_CREATE_SALE, method: "POST", body }),
      invalidatesTags: ["companySales"],
    }),

    // ── Decision (direct, non-workflow path) ────────────────────────────────
    // Approve / reject / request-changes in one endpoint, mirroring the expense
    // decision contract. Companies with a workflow template configured route
    // through the workflow engine's own approve/reject endpoints instead.
    companyDecideSale: builder.mutation<
      IApiResponse<ISale>,
      { id: number; payload: ISaleDecisionPayload }
    >({
      query: ({ id, payload }) => ({
        url: API_COMPANY_SALE_DECISION(id),
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["companySales", "companySale", "companyCommissions"],
    }),

    /** Restate an already-decided sale's commission. Reason is mandatory. */
    companyOverrideSaleCommission: builder.mutation<
      IApiResponse<ISale>,
      { id: number; payload: ICommissionOverridePayload }
    >({
      query: ({ id, payload }) => ({
        url: API_COMPANY_SALE_COMMISSION_OVERRIDE(id),
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["companySales", "companySale", "companyCommissions"],
    }),

    /** Server-side commission calculation, used to confirm the UI's preview. */
    companyPreviewCommission: builder.query<
      IApiResponse<ICommissionPreviewResult>,
      ICommissionPreviewParams
    >({
      query: (params) => ({ url: API_COMPANY_SALE_COMMISSION_PREVIEW, params }),
    }),

    // ── Commission tracking ─────────────────────────────────────────────────
    companyGetCommissionsByEmployee: builder.query<
      IApiPaginatedResponse<ICommissionByEmployee[]>,
      IListCommissionsParams
    >({
      query: (params) => ({
        url: API_COMPANY_COMMISSIONS_BY_EMPLOYEE,
        params,
      }),
      providesTags: ["companyCommissions"],
    }),
    companyGetCommissionSummary: builder.query<
      IApiResponse<ICommissionSummaryData>,
      { saleDateFrom?: string; saleDateTo?: string } | void
    >({
      query: (params) => ({
        url: API_COMPANY_COMMISSIONS_SUMMARY,
        params: params ?? undefined,
      }),
      providesTags: ["companyCommissions"],
    }),

    // ── Commission plans (module settings) ──────────────────────────────────
    companyGetCommissionPlans: builder.query<
      IApiResponse<ICommissionPlan[]>,
      { search?: string; active?: boolean } | void
    >({
      query: (params) => ({
        url: API_COMPANY_COMMISSION_PLANS,
        params: params ?? undefined,
      }),
      providesTags: ["commissionPlans"],
    }),
    companyGetCommissionPlanById: builder.query<
      IApiResponse<ICommissionPlan>,
      number
    >({
      query: (id) => ({ url: API_COMPANY_COMMISSION_PLAN(id) }),
      providesTags: ["commissionPlan"],
    }),
    companyCreateCommissionPlan: builder.mutation<
      IApiResponse<ICommissionPlan>,
      ICreateCommissionPlanPayload
    >({
      query: (body) => ({
        url: API_COMPANY_COMMISSION_PLANS,
        method: "POST",
        body,
      }),
      // A new plan can change which plan future sales resolve to.
      invalidatesTags: ["commissionPlans", "companySales"],
    }),
    companyUpdateCommissionPlan: builder.mutation<
      IApiResponse<ICommissionPlan>,
      { id: number; payload: IUpdateCommissionPlanPayload }
    >({
      query: ({ id, payload }) => ({
        url: API_COMPANY_COMMISSION_PLAN(id),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["commissionPlans", "commissionPlan", "companySales"],
    }),
    companyDeleteCommissionPlan: builder.mutation<
      IApiResponse<{ deleted: boolean }>,
      number
    >({
      query: (id) => ({
        url: API_COMPANY_COMMISSION_PLAN(id),
        method: "DELETE",
      }),
      invalidatesTags: ["commissionPlans"],
    }),
  }),
});

export const {
  useCompanyGetSalesQuery,
  useCompanyGetSaleStatsQuery,
  useCompanyGetSaleByIdQuery,
  useCompanyCreateSaleMutation,
  useCompanyDecideSaleMutation,
  useCompanyOverrideSaleCommissionMutation,
  useCompanyPreviewCommissionQuery,
  useCompanyGetCommissionsByEmployeeQuery,
  useCompanyGetCommissionSummaryQuery,
  useCompanyGetCommissionPlansQuery,
  useCompanyGetCommissionPlanByIdQuery,
  useCompanyCreateCommissionPlanMutation,
  useCompanyUpdateCommissionPlanMutation,
  useCompanyDeleteCommissionPlanMutation,
} = companySalesApi;
